/* eslint-disable react/no-unknown-property */
import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { FILE_MIME_TYPE_EXTENSIONS, FILE_TYPES } from "@miplatform/constants";
import { base64ToBlobUrl, getRootUrl } from "@miplatform/utils";
import { mdiFileDocumentOutline } from "@mdi/js";
import { Box, Link, Typography, useTheme } from "@mui/material";
import { MiLoader } from "../MiLoader/MiLoader";
import { MiPlaceholderList } from "../MiPlaceholderList/MiPlaceholderList";

export const DocumentPreview = ({ selectedDocument }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const [isPdfError, setIsPdfError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (selectedDocument?.base64Data) {
      const url = base64ToBlobUrl(
        selectedDocument.base64Data,
        selectedDocument.contentType
      );
      setObjectUrl(url);

      return () => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      };
    }
    setObjectUrl(null);
    // for satisfying linter
    return undefined;
  }, [selectedDocument?.base64Data]);

  const documentUrl = useMemo(() => {
    if (objectUrl) {
      return objectUrl;
    }
    return selectedDocument?.referenceId
      ? `${getRootUrl()}/${selectedDocument.referenceId}`
      : null;
  }, [objectUrl, selectedDocument?.referenceId]);

  if (!selectedDocument) {
    return (
      <MiPlaceholderList
        icon={mdiFileDocumentOutline}
        iconColor={theme.palette.primary.main}
        iconBgColor={theme.palette.primary.light}
        text="Document Preview"
        helperText="Select a document to preview its contents."
      />
    );
  }

  const fileType =
    FILE_MIME_TYPE_EXTENSIONS[selectedDocument.contentType]?.toUpperCase();

  if (fileType === FILE_TYPES.PDF) {
    if (isPdfError) {
      return (
        <Box>
          <Typography mb={2}>
            Unable to display PDF directly. You can:
          </Typography>
          <Link
            href={
              documentUrl || `${getRootUrl()}/${selectedDocument.referenceId}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Open PDF in new tab
          </Link>
        </Box>
      );
    }

    return (
      <>
        <MiLoader
          style={{ position: "absolute", top: "50%" }}
          isLoading={isLoading || !documentUrl}
        />
        <object
          data={documentUrl}
          type="application/pdf"
          width="100%"
          style={{ borderRadius: 8 }}
          height="100%"
          aria-label="PDF document"
          onLoad={() => {
            setIsLoading(false);
          }}
          onError={() => {
            setIsPdfError(true);
            setIsLoading(false);
          }}
        />
      </>
    );
  }

  if (fileType === FILE_TYPES.MP4) {
    return (
      <video
        src={documentUrl}
        controls
        type="video/mp4"
        width="100%"
        height="100%"
        className={classes.pdfViewer}
      >
        <track kind="captions" default />
      </video>
    );
  }

  if ([FILE_TYPES.JPEG, FILE_TYPES.PNG, FILE_TYPES.JPG].includes(fileType)) {
    return (
      <img
        src={documentUrl}
        alt={selectedDocument.name}
        className={classes.imagePreview}
      />
    );
  }

  return (
    <MiPlaceholderList
      icon={mdiFileDocumentOutline}
      iconColor={theme.palette.primary.main}
      iconBgColor={theme.palette.primary.light}
      text="Unable to Preview Document"
      helperText="Preview not available for this file type."
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 500,
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(4),
  },
  pdfViewer: {
    width: "100%",
    height: "100%",
    border: "none",
  },
  imagePreview: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
}));
